import { cloneElement, ReactElement, ReactNode, useState } from "react";

import {
  ArrowUpRight,
  DownloadFill,
  Upload as UploadIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  UploadStatus,
  useUploadManager,
} from "@hl/base-components/lib/upload/context";
import {
  Anchor,
  Box,
  Button,
  createStyles,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { FileWithPath } from "@mantine/dropzone";
import { BlobReader, Entry, ZipReader } from "@zip.js/zip.js";
import { FileError, FileRejection } from "react-dropzone";

import { SUPPORT_URL } from "config";
import { DraftProgress } from "~/apollo/graphql.generated";

import FormTitle from "../FormTitle";
import NextStepButton from "../NextStepButton";
import SaveDraftButton from "../SaveDraftButton";
import ZipUpload, { InternalState } from "../ZipUpload";
import {
  CreateCollectionForm,
  CreateCollectionState,
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "../context";
import useSaveDraft from "../useSaveDraft";

import { PackageTreePreview } from "./PackageTreeView";

export enum ERROR_CODES {
  INVALID_DIRECTORY = "INVALID_DIRECTORY",
  MISSING_HL_SCRIPT = "MISSING_HL_SCRIPT",
  MISSING_INDEX = "MISSING_INDEX",
}

const useStyles = createStyles({
  link: {
    textDecoration: "none",
    fontSize: 14,
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

export const isCodeGenZipInvalid = (
  formValues: CreateCollectionForm,
  state: CreateCollectionState
) => {
  return !formValues.metadata.mediaUrl || !state.unzipped;
};

export const CodeGenZipUpload = () => {
  const { classes } = useStyles();
  const { dispatch, state } = useCreateCollectionState();
  const form = useCreateCollectionFormContext();

  const [hasErrors, setHasErrors] = useState(false);
  const [internalUploadState, setInternalUploadState] =
    useState<InternalState>("not-started");

  const { saveDraft, loading } = useSaveDraft({
    furthestStep: DraftProgress.ADDED_METADATA,
  });

  const onNextStep = () => {
    dispatch({
      type: "NEXT_STEP",
      payload: {
        form,
      },
    });
  };

  const zipFileValidation = async (
    files: FileWithPath[]
  ): Promise<FileRejection[]> => {
    const file = files[0];
    const errors: FileError[] = [];
    const zipReader = new ZipReader(new BlobReader(file));
    const entries = (await zipReader.getEntries()).filter(
      (entry) =>
        !entry.filename.match(/^__MACOSX\/|\.DS_Store|^readme\.pdf|^example.+/)
    );

    // if (entries.at(0)?.directory) {
    //   errors.push({
    //     code: ERROR_CODES.INVALID_DIRECTORY,
    //     message:
    //       "zip file should not contain a directory at the root level. Please compress the contents of the directory into a .zip file and try again.",
    //   });
    // } else {
    if (!entries.find((entry) => entry.filename === "index.html")) {
      errors.push({
        code: ERROR_CODES.MISSING_INDEX,
        message: "package is missing index.html file",
      });
    }

    if (!entries.find((entry) => entry.filename.includes("hl-gen.js"))) {
      errors.push({
        code: ERROR_CODES.MISSING_HL_SCRIPT,
        message:
          "hl-gen.js file not found in the .zip you uploaded. " +
          "This indicates that your code will not be deterministic and people may end up with identical tokens " +
          "or with tokens that look different every time they are rendered. " +
          "We strongly recommend including the hl-gen.js file and using the provided random functions to create deterministic randomness. " +
          "Read the docs for more information.",
      });
    }
    //}

    if (!errors.length) {
      return [];
    }

    return [
      {
        file: files[0],
        errors,
      },
    ];
  };

  const onMediaChanged = (url: string | null) => {
    dispatch({
      type: "UPDATE_SERIES",
      payload: {
        newMediaUrl: url,
      },
    });
  };

  const isDisabled = isCodeGenZipInvalid(form.values, state);

  const handleEntries = async (entries: Entry[]) => {
    const files = entries.map((entry) => entry.filename);
    form.setFieldValue("metadata", {
      ...form.values.metadata,
      mediaFiles: files,
    });
  };
  const { state: uploadState, dispatch: uploadDispatch } = useUploadManager();
  const packageFiles = form.values.metadata.mediaFiles ?? [];
  const showZipPreview =
    ![UploadStatus.NOT_STARTED, UploadStatus.ERROR].includes(
      uploadState.status
    ) || packageFiles.length;

  const resetUpload = () => {
    form.setFieldValue("metadata", {
      ...form.values.metadata,
      mediaUrl: null,
      mediaFiles: undefined,
    });
    uploadDispatch({
      type: "UPLOAD_RESET",
    });
    dispatch({
      type: "UPDATE_UNZIPPED",
      payload: {
        unzipped: false,
      },
    });
    setHasErrors(false);
  };

  return (
    <Box mx={"auto"}>
      <FormTitle
        title="Upload assets"
        buttons={[
          <SaveDraftButton key="draft" onClick={saveDraft} loading={loading} />,
          <NextStepButton
            key="next"
            onClick={onNextStep}
            loading={loading}
            disabled={isDisabled}
          />,
        ]}
      />
      <Stack spacing={0}>
        <Text size="sm" mb="xl">
          To get started, upload a .zip file that contains the contents of your
          project. By uploading, you agree to comply with{" "}
          <Anchor
            target="_blank"
            rel="noreferrer"
            className={classes.link}
            href="https://support.highlight.xyz/knowledge-base/get-started/highlight-policy"
          >
            Highlight`&apos;s community guidelines
          </Anchor>
          ,{" "}
          <Anchor
            target="_blank"
            rel="noreferrer"
            className={classes.link}
            href="https://highlight.xyz/terms-of-service"
          >
            terms of service
          </Anchor>{" "}
          and{" "}
          <Anchor
            target="_blank"
            rel="noreferrer"
            className={classes.link}
            href="https://highlight.xyz/privacy-policy"
          >
            privacy policy
          </Anchor>
          .
        </Text>
        <Group position="apart" align="stretch" noWrap spacing="lg" mb={40}>
          <GuideCard
            title="Read the docs"
            description="Learn how to create generative art on Highlight, including how
                to access data from the blockchain, generate deterministic
                randomness, set metadata, and more."
            icon={<ArrowUpRight />}
            link={SUPPORT_URL.GEN_ART_REPO}
          />
          <GuideCard
            title="Download an example project"
            description="Explore our example project to see an illustration of how to include the hl-gen.js script, a third-party library, organize a project, and more."
            icon={<DownloadFill />}
            link={SUPPORT_URL.GEN_ART_EXAMPLE}
          />
        </Group>
        {(!showZipPreview || !state.unzipped) && (
          <ZipUpload
            validate={zipFileValidation}
            handleEntries={handleEntries}
            mediaError={(form.errors["metadata.mediaUrl"] as string) ?? ""}
            onMediaChanged={onMediaChanged}
            onResetUpload={resetUpload}
            onReject={() => setHasErrors(true)}
            onInternalStateChange={setInternalUploadState}
            warningErrorCodes={[ERROR_CODES.MISSING_HL_SCRIPT]}
          />
        )}
        {showZipPreview ? (
          <Stack spacing={20} mt={40}>
            <Group position="apart">
              <Title order={6} fw={500}>
                Review your upload
              </Title>
              {/* Don't show button when there is errors (error view should give a way to try again), and
               when fetching URLs as the request is not cancellable for now */}
              {!hasErrors && internalUploadState !== "fetching-urls" && (
                <Button color={SECONDARY_COLOR} onClick={resetUpload} px={12}>
                  <Group spacing={8}>
                    <UploadIcon width={16} /> Upload new .zip
                  </Group>
                </Button>
              )}
            </Group>

            {packageFiles.length !== 0 && (
              <PackageTreePreview files={packageFiles} />
            )}
          </Stack>
        ) : (
          <></>
        )}
      </Stack>
      <NextStepButton
        onClick={onNextStep}
        loading={loading}
        mt={40}
        fullWidth
        disabled={isDisabled}
      />
    </Box>
  );
};

const GuideCard = ({
  title,
  description,
  icon,
  link,
}: {
  title: ReactNode;
  description: ReactNode;
  icon: ReactElement<{ width: number; height: number }>;
  link: string;
}) => (
  <Anchor href={link} target="_blank" underline={false}>
    <Paper p="lg" shadow="sm" h="100%">
      <Group spacing={20} noWrap>
        <Stack spacing={8}>
          <Text size="xl" fw={WEIGHT_BOLD}>
            {title}
          </Text>
          <Text size="sm">{description}</Text>
        </Stack>
        <Box>{cloneElement(icon, { width: 20, height: 20 })}</Box>
      </Group>
    </Paper>
  </Anchor>
);
